import { dateCellRenderer, userOrgCellRenderer } from "components/cell-renders";
import { DateUtils } from "utils/dateutils";

// Every column formatter below is best-effort: the grid hands us loosely typed
// row data, so a missing nested field must degrade to an empty cell rather than
// abort the whole CSV export.
const withFallback = (compute: () => any) => {
  try {
    return compute();
  } catch (ex) {
    console.error(ex);
    return "";
  }
};

const joinNames = (items: any) =>
  (items || []).map((item: any) => item.name).join("\n");

const formatLinkedTasks = (value: any) =>
  (value || [])
    .filter((item: any) => !item.deleted_from_source)
    .map((item: any) =>
      item.activity_id
        ? `${item.activity_id} - ${item.task_name}`
        : item.task_name
    )
    .join("\n");

const formatSubmittalReference = (id: any, data: any, context: any) =>
  context.projectDetails?.spec_section && data.spec_no
    ? `${data.spec_no} - ${id}`
    : `${id}`;

const formatStatus = (value: any, column: any, context: any) => {
  const dataMap = column.colDef.cellRendererParams.dataMapVar;
  const fieldLabel = column.colDef.cellRendererParams.labelVar;
  return context[dataMap][value][fieldLabel];
};

const formatSubmittalAssignee = (data: any, column: any) => {
  const userData = userOrgCellRenderer({
    data,
    userInput: column.userProvidedColDef.cellRendererParams.userInput
  });
  const userFullName = userData?.fullName || "";
  if (!userFullName?.trim()) return "";
  return `${userFullName} (${userData.userOrg || ""})`;
};

const formatCustomSpecSection = (data: any, value: any) => (data ? value : "");

const formatSubmittalDueDate = (data: any) =>
  data.date_block_submittals.length
    ? DateUtils.format(data.date_block_submittals[0].due_date)
    : "";

const formatSubmittalRiskAssessment = (data: any) =>
  data?.date_block_submittals?.length
    ? data.date_block_submittals[0].risk_assessment
    : "";

const formatSubmittalLinkedMaterials = (
  value: any,
  data: any,
  context: any
) => {
  const materials = value || [];
  if (materials.length === 1 && materials[0].implicit) {
    const submittalRefrenceId = formatSubmittalReference(
      data.submittal_id,
      data,
      context
    );
    return `Material for Submittal ${submittalRefrenceId}`;
  }
  return joinNames(materials.filter((material: any) => !material.implict));
};

export const getSubmittalFormattedValues = (params: any) => {
  const { value, column, node, context } = params;
  const { data } = node;

  switch (true) {
    case column.colId === "submittal_id":
      return withFallback(() => formatSubmittalReference(value, data, context));
    case column.colId === "status":
      return withFallback(() => formatStatus(value, column, context));
    case column.colId === "assignee":
      return withFallback(() => formatSubmittalAssignee(data, column));
    case column.colId === "custom_spec_section":
      return withFallback(() => formatCustomSpecSection(data, params.value));
    case column.colId === "dueDate":
      return formatSubmittalDueDate(data);
    case column.colId === "risk_assessment":
      return formatSubmittalRiskAssessment(data);
    case column.colId === "linked_tasks":
      return withFallback(() => formatLinkedTasks(value));
    case column.colId === "linked_materials":
      return withFallback(() =>
        formatSubmittalLinkedMaterials(value, data, context)
      );
    case column.colId === "BIC_GroupBy":
      return withFallback(() => data.responsible_contractor_name);
    default:
      return value ?? "";
  }
};

const formatMaterialSpecSection = (data: any) => {
  let specSectionStr = data.spec_section_no || "";
  if (data.spec_section_name) {
    specSectionStr += ` - ${data.spec_section_name}`;
  }
  return specSectionStr;
};

const formatMaterialAssignee = (data: any, column: any) => {
  const userData = userOrgCellRenderer({
    data,
    userInput: column.userProvidedColDef.cellRendererParams.userInput
  });
  return userData.userOrg
    ? `${userData.fullName} (${userData.userOrg})`
    : userData.fullName;
};

const formatMaterialDueDate = (data: any) =>
  data?.date_block_materials.length
    ? DateUtils.format(data.date_block_materials[0]?.due_date)
    : "";

const formatMaterialRiskAssessment = (data: any) =>
  data?.date_block_materials?.length
    ? data.date_block_materials[0].risk_assessment
    : "";

const formatOptionalDate = (value: any) =>
  value ? DateUtils.format(value) : "";

export const getMaterialFormattedValues = (params: any) => {
  const { value, column, node } = params;
  const { data } = node;

  switch (true) {
    case column.colId === "custom_spec_section":
      return withFallback(() => formatMaterialSpecSection(data));
    case column.colId === "name":
      return withFallback(() => node.data.name || "");
    case column.colId === "dueDate":
      return formatMaterialDueDate(data);
    case column.colId === "assignee" || column.colId === "gc_representative":
      return withFallback(() => formatMaterialAssignee(data, column));
    case column.colId === "linked_tasks":
      return withFallback(() => formatLinkedTasks(value));
    case column.colId === "linked_submittals":
      return withFallback(() => joinNames(value));
    case column.colId === "linked_materials":
      return withFallback(() => joinNames(value));
    case column.colId === "next_planned_deadline":
      return withFallback(() => formatOptionalDate(params.value));
    case column.colId === "risk_assessment":
      return formatMaterialRiskAssessment(data);
    case column.colDef.cellRenderer === dateCellRenderer:
      return withFallback(() => formatOptionalDate(value));
    case column.colId === "BIC_GroupBy":
      return withFallback(() => data.responsible_contractor_name);
    default:
      return value ?? "";
  }
};
